/**
 * Given a const array of strings that represent a union of string type,
 * generate a type guard that will return true if the input is one of the
 * strings in the array.
 *
 * Consider use `as const` after your array if you have an error!
 *
 * @example
 * const electricCars = ['Tesla', 'Volt', 'Taycan'] as const;
 * type ElectricCar = typeof electricCars[number];
 *
 * const isCarMake = buildTypePredicate(electricCars);
 *
 * function charge(car: ElectricCar) {
 *   //...
 * }
 *
 * const maybeElectric: string = 'Tesla';
 * if (isCarMake(maybeElectric)) {
 *   // maybeElectric is now narrowed to type ElectricCar and will satisfy the type
 *   charge(maybeElectric);
 * }
 */
export const buildTypePredicate =
  <T extends string>(validStrings: readonly [T, ...T[]]) =>
  (input: string | null | undefined): input is T => {
    return input != null && validStrings.includes(input as T);
  };

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
      ? RecursivePartial<T[P]>
      : T[P];
};

/**
 * For a complicated type which may be a combination of multiple types,
 * simplify the type to be more legible.
 */
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

/**
 * Given an object, get the value of all keys on the object.
 */
export type ValuesOf<Obj> = Obj[keyof Obj];

type OneOnly<Obj, Key extends keyof Obj> = {
  [key in Exclude<keyof Obj, Key>]+?: undefined;
} & Pick<Obj, Key>;
type OneOfByKey<Obj> = { [key in keyof Obj]: OneOnly<Obj, key> };

/**
 * Given an object with keys and values, return a type
 * where only one of the keys is allowed to be present.
 */
export type OneOf<Obj> = ValuesOf<OneOfByKey<Obj>>;
